import axios, {
  type AxiosError,
  type AxiosInstance,
  type InternalAxiosRequestConfig,
} from 'axios'
import { isApiProblem, type ApiProblem } from './problem'

/** Base URL. Same-origin `/api` in production behind nginx; Vite proxies it in dev. */
const BASE_URL = import.meta.env.VITE_API_BASE_URL ?? '/api'

/** Where the access token lives: in memory only, never localStorage. */
let accessToken: string | null = null

export function setAccessToken(token: string | null) {
  accessToken = token
}

export function getAccessToken() {
  return accessToken
}

type RetriableConfig = InternalAxiosRequestConfig & { _retried?: boolean }

/** Called when refreshing fails, so the app can clear state and route to /login. */
let onAuthFailure: (() => void) | null = null

export function setAuthFailureHandler(handler: () => void) {
  onAuthFailure = handler
}

export const apiClient: AxiosInstance = axios.create({
  baseURL: BASE_URL,
  timeout: 30_000,
  // Required so the HttpOnly refresh cookie is sent to /api/v1/auth.
  withCredentials: true,
  headers: { 'Content-Type': 'application/json' },
})

apiClient.interceptors.request.use((config) => {
  if (accessToken) {
    config.headers.Authorization = `Bearer ${accessToken}`
  }
  // A file upload is not JSON, and the instance default says it is.
  //
  // Axios only picks the content type itself when the header is unset, and this instance sets
  // it for every request — so a FormData body went out labelled application/json and Spring
  // refused it with a 415 before any handler ran. Deleting the header lets the browser write
  // the multipart type *with its boundary*, which is the part nothing else can supply: setting
  // "multipart/form-data" by hand produces a body the server cannot split.
  if (typeof FormData !== 'undefined' && config.data instanceof FormData) {
    delete config.headers['Content-Type']
  }
  // One id per request, echoed back by the server and present in its logs — so a user
  // can screenshot an error and support can find the exact server-side trace.
  config.headers['X-Correlation-Id'] = crypto.randomUUID()

  const language = document.documentElement.lang
  if (language) {
    config.headers['Accept-Language'] = language
  }
  return config
})

/**
 * The refresh response. Carries the user too, so a session restored on page load needs
 * only this one call.
 */
export interface RefreshResult {
  accessToken: string
  user: unknown
  mustChangePassword: boolean
}

/*
 * Single-flight refresh — one in-flight call, shared by every caller.
 *
 * Two separate things converge on this:
 *
 *   1. A page fires several requests, the token has just expired, and all of them get a
 *      401. Without sharing, each would call /auth/refresh.
 *   2. The app boots and restores its session, while a request fired in the same tick
 *      also 401s.
 *
 * Either way, concurrent refreshes are actively harmful rather than merely wasteful:
 * refresh tokens rotate, so the second call presents a token the first has already
 * spent. Depending on timing the server answers 409 (two writers on one row) or treats
 * it as token theft and burns the whole family — and the user is silently signed out.
 * That is exactly what happened on every page reload, because React invokes effects
 * twice in development and two tabs do the same thing in production.
 */
let refreshInFlight: Promise<RefreshResult> | null = null

export async function refreshSession(): Promise<RefreshResult> {
  refreshInFlight ??= axios
    .post<RefreshResult>(`${BASE_URL}/v1/auth/refresh`, {}, { withCredentials: true })
    .then((response) => {
      setAccessToken(response.data.accessToken)
      return response.data
    })
    .finally(() => {
      refreshInFlight = null
    })

  return refreshInFlight
}

apiClient.interceptors.response.use(
  (response) => response,
  async (error: AxiosError<ApiProblem>) => {
    const config = error.config as RetriableConfig | undefined
    const status = error.response?.status

    // Never try to refresh a failed refresh, or a failed login — that would loop.
    const isAuthEndpoint =
      config?.url?.includes('/auth/refresh') || config?.url?.includes('/auth/login')

    if (status === 401 && config && !config._retried && !isAuthEndpoint) {
      config._retried = true
      try {
        const { accessToken } = await refreshSession()
        config.headers.Authorization = `Bearer ${accessToken}`
        return apiClient(config)
      } catch {
        setAccessToken(null)
        onAuthFailure?.()
        return Promise.reject(error)
      }
    }

    if (status === 401 && isAuthEndpoint && config?.url?.includes('/auth/refresh')) {
      setAccessToken(null)
      onAuthFailure?.()
    }

    return Promise.reject(error)
  },
)

/** Narrows an unknown thrown value to the backend's problem payload, if it is one. */
export function toApiProblem(error: unknown): ApiProblem | null {
  if (axios.isAxiosError(error) && isApiProblem(error.response?.data)) {
    return error.response.data
  }
  return null
}

/**
 * Downloads a file through the authenticated API rather than a plain link, so exports
 * respect permissions and the bearer token.
 */
export async function downloadFile(url: string, filename: string, params?: unknown) {
  const response = await apiClient.get(url, { params, responseType: 'blob' })
  const href = URL.createObjectURL(response.data as Blob)
  const anchor = document.createElement('a')
  anchor.href = href
  anchor.download = filename
  document.body.appendChild(anchor)
  anchor.click()
  anchor.remove()
  URL.revokeObjectURL(href)
}
